import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './VideoPlayer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * VideoPlayer component with lazy loading and thumbnail preview
 * Only loads the video when the user clicks play
 * Uses streaming endpoint for efficient video delivery
 */
const VideoPlayer = ({ video, className = '', showTitle = true, linkToDetail = true, autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const videoRef = useRef(null);
  const navigate = useNavigate();

  // Get thumbnail URL
  const getThumbnailUrl = () => {
    if (video.thumbnail) {
      return video.thumbnail.startsWith('http') 
        ? video.thumbnail 
        : `${API_URL}${video.thumbnail}`;
    }
    return 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg';
  };

  // Get streaming video URL
  const getVideoUrl = () => {
    // Use streaming endpoint for better performance
    return `${API_URL}/api/videos/stream/${video.id}/`;
  };

  // Handle play button click
  const handlePlayClick = () => {
    if (linkToDetail) {
      // Navigate to detail page for this video
      navigate(`/video/${video.id}`);
      return;
    }

    setHasLoaded(true);
    setIsPlaying(true);

    // Small delay to ensure video element is rendered
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(error => {
          console.error('Error playing video:', error);
        });
      }
    }, 100);
  };

  // If autoPlay is requested (used on detail page), load video immediately
  useEffect(() => {
    if (autoPlay) {
      setHasLoaded(true);
      // try to play after render
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(() => {});
        }
      }, 150);
    }
  }, [autoPlay]);

  // Handle video pause
  const handlePause = () => {
    setIsPlaying(false);
  };

  // Handle video play
  const handlePlay = () => {
    setIsPlaying(true);
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`video-player-container ${className}`}>
      <div className="video-wrapper">
        {!hasLoaded ? (
          // Show thumbnail with play button overlay
          <div className="video-thumbnail-container">
            <img 
              src={getThumbnailUrl()} 
              alt={video.title}
              className="video-thumbnail"
            />
            <button 
              className="video-play-button"
              onClick={handlePlayClick}
              aria-label="Play video"
            >
              <svg 
                className="play-icon" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z"/>
              </svg>
            </button>
            {video.duration && (
              <div className="video-duration-badge">
                {formatDuration(video.duration)}
              </div>
            )}
          </div>
        ) : (
          // Show actual video player
          <video
            ref={videoRef}
            className="video-element"
            controls
            poster={getThumbnailUrl()}
            onPause={handlePause}
            onPlay={handlePlay}
            preload="metadata"
          >
            <source src={getVideoUrl()} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
      
      {showTitle && (
        <div className="video-info">
          <h3 className="video-title">{video.title}</h3>
          {video.category && (
            <span className={`video-category ${video.category.toLowerCase()}`}>
              {video.category}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
